/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {loadDevices} from '@ion/core/src/services/Connect';
import {
  AssetSearchableProperty,
  DeviceFilter,
  DeviceSearchableProperty,
  LoadDevicesParams
} from '@ion/core/src/types/services';
import {mapDataToGroups} from '../../filters/TreeFilterUtils';
import {normalizeAccountIds} from './TreeViewUtils';

const PAGE_SIZE = 100;

interface Status {
  [groupId: string | number]: {
    completed: boolean,
    remaining?: number,
    groupKey?: string,
    loaded: number,
    page: number
  }
}

interface RequestDeviceSearchParams {
  groupNameId: string,
  currentGlobalGroup: any,
  deviceFilter: any,
  maxResultLength: number
}

/**
 * The DeviceLoader provides a centralized API for loading Devices for individual
 * Groups for subsequent insertion into the CVS or HVS Trees, determining the current
 * state for each group in terms of loading devices.
 *
 * @class DeviceLoader
 */
class DeviceLoader {
  /**
   * Provides the mappings of group ids to their current device loading status.
   * Each value represents the status of the group, and has the following form:
   *
   * loaded    : <number of devices that have currently been loaded>
   * page      : <next page to be loaded, or current page if all devices loaded>
   * completed : <a flag to indicate if device loading has been completed>
   * groupKey  : <the tree data stucture key>
   * remaining : <the number of devices remaining to be loaded>
   *
   * @private
   */
  _status = {};

  /**
   * @method loadDevices
   * @param  groupInfo {object} A group info object created via getGroupInfo
   *         which contains a groupKey and groupId property.
   */
  async loadDevices(groupId: number, groupKey: string, filterOn: DeviceFilter) {
    const status = this.getGroupStatus(groupId, groupKey);
    if (!status?.completed) {
      const {pageSize, sort} = DeviceLoader.request;
      const params: LoadDevicesParams = {
        accountId : groupId,
        pageId    : status.page,
        sortOn    : sort,
        filterOn,
        pageSize
      };
      const results                = await loadDevices(params);
      // if device has an associated asset, it should not be included.
      const filtered = results?.devices?.filter(({asset}) => !asset);
      const {completed, remaining} = this._setGroupStatus(status, results);
      return {devices: filtered, completed, remaining};
    }
    return Promise.resolve({devices: [], completed: true, remaining: 0});
  }

  /**
   * @method getGroupStatus
   * @param  groupInfo {object} A group info object created via getGroupInfo
   *         which contains a groupKey and groupId property.
   */
  getGroupStatus(groupId, groupKey) {
    let status = this._status[groupId];
    if (!status && groupKey) {
      status = this._status[groupId] = {
        loaded : 0, page : 1, completed : false, groupKey
      };
    }
    return status;
  }

  /**
   * @method getStatus
   */
  getStatus(): Status {
    return this._status;
  }

  /**
   * @method requestSearch
   */
  async requestSearch({
    groupNameId,
    currentGlobalGroup,
    deviceFilter,
    maxResultLength: pageSize
  }: RequestDeviceSearchParams) {
    const param = {
      accountId        : normalizeAccountIds(currentGlobalGroup),
      includeSubGroups : true,
      filterOn         : {
        [AssetSearchableProperty.name]  : `*${groupNameId}*`,
        [DeviceSearchableProperty.type] : deviceFilter
      },
      includeGroups : true,
      pageSize      : pageSize ?? 10000 // this number is subjected to change on Impl. of pagination
    };
    // request device search
    const {devices} = await loadDevices(param);
    const filtered = devices?.filter(({asset}) => !asset);
    const devicesFound = mapDataToGroups({
      data : filtered, type : 'devices'
    });
    // filter devices by display only within the current global group
    return devicesFound.filter(({id}) => currentGlobalGroup[id]);
  };

  /**
   * @private
   * @method _setGroupStatus
   */
  _setGroupStatus(status, {totalCount, devices}): Status {
    const retrieved  = status.loaded + devices.length;
    status.loaded    = retrieved;
    status.remaining = totalCount - retrieved;
    if (retrieved >= totalCount) {
      status.completed = true;
    } else {
      status.page = ++status.page;
    }
    return status;
  }

  /**
   * @method reset
   */
  reset() {
    this._status = {};
  }

  /**
   * @static
   */
  static request = {
    pageSize : PAGE_SIZE,
    sort     : ['+name']
  };
};

export default new DeviceLoader();
